import { Col, Divider, Form, Row, Select, Typography } from "antd"
import React from "react"

import { PhoneInput } from "../../../../../components/PhoneInputs/PhoneInput.tsx"
import { placeholders } from "../../../../../constants/placeholders"
import { validatePhone } from "../../../../../utils/validation/validate-phone.ts"
import { zipCodeRegexUS } from "../../../../../utils/validation/zip-code.ts"
import { proposalFilterOptions } from "../../../../Proposals/constants/proposal-filter-options.ts"
import { type TemplateDocumentFormData } from "../../../types/template-document-form-data.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const { Title } = Typography

interface CustomerInfoBlockProps {
  readonly?: boolean
  disabled?: boolean
}

export function CustomerInfoBlock({
  readonly,
  disabled
}: CustomerInfoBlockProps) {
  const fieldKey: keyof TemplateDocumentFormData = "customer"

  const disabledField = readonly || disabled

  return (
    <>
      <Row gutter={[12, 0]}>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "first_name"]}
            label="First Name"
          >
            <TextInput disabled={disabledField} placeholder="Enter first name" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "middle_name"]}
            label="Middle Name"
          >
            <TextInput disabled={disabledField} placeholder="Enter middle name" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "last_name"]}
            label="Last Name"
          >
            <TextInput disabled={disabledField} placeholder="Enter last name" />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "email"]}
            label="Email"
            rules={[
              { type: "email", message: "The email you entered is incorrect" }
            ]}
          >
            <TextInput disableAutoCapitalize disabled={disabledField} placeholder={placeholders.email} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "phone"]}
            label="Phone"
            rules={[{ required: false, validator: validatePhone }]}
          >
            <PhoneInput disabled={disabledField} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "phone_2"]}
            label="Phone 2"
            rules={[{ required: false, validator: validatePhone }]}
          >
            <PhoneInput disabled={disabledField} />
          </Form.Item>
        </Col>
      </Row>

      {/* PROPERTY INFORMATION */}

      <div className={"pt-2"}>
        <Title level={5}>Property Information</Title>
        <Divider size={"small"} />
      </div>

      <Row gutter={[12, 0]}>
        <Col span={12}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "address"]}
            label="Address"
          >
            <TextInput disabled={disabledField} placeholder="Enter address" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "address_2"]}
            label="Address 2"
          >
            <TextInput disabled={disabledField} placeholder="Enter address" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "city"]}
            label="City"
          >
            <TextInput disabled={disabledField} placeholder="Enter city" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "state"]}
            label="State"
            normalize={(value: string) => value ?? ""}
            getValueProps={(value: string) => ({ value: value || null })}
          >
            <Select
              disabled={disabledField}
              allowClear
              placeholder={"Enter state"}
              options={proposalFilterOptions.state}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<TemplateDocumentFormData>
            name={[fieldKey, "zip_code"]}
            label="Zip Code"
            rules={[
              {
                required: false,
                pattern: zipCodeRegexUS,
                message: "Invalid postal code format"
              }
            ]}
          >
            <TextInput
              disableAutoCapitalize
              disabled={disabledField}
              placeholder={placeholders.zipCode}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}
